import { useEffect, useState, useRef } from 'react';
import { Radio, AlertTriangle, Users, RefreshCw } from 'lucide-react';
import { WS_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';
import type { LiveMeetingsData } from './overview-types';

interface Props {
  data: LiveMeetingsData;
  accountId?: string;
}

export function LiveMeetingsSection({ data: initialData, accountId }: Props) {
  const [data, setData] = useState(initialData);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    setData(initialData);
    setLastUpdated(new Date());
  }, [initialData]);

  useEffect(() => {
    const token = getToken();
    if (!token) return;

    const params = new URLSearchParams({ token });
    if (accountId) params.set('accountId', accountId);
    params.set('interval', '30');

    const ws = new WebSocket(`${WS_BASE}/zoom/meetings/live/stream?${params}`);
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'update') {
          const meetings = typeof msg.meetings === 'string' ? JSON.parse(msg.meetings) : msg.meetings || [];
          setData({ _scope: 'admin', totalLive: msg.totalLive ?? 0, meetings });
          setLastUpdated(new Date(msg.timestamp || Date.now()));
        }
      } catch {
        // Ignore parse errors
      }
    };

    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [accountId]);

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-3 border-b border-border px-5 py-4">
        <Radio className={`h-5 w-5 ${data.totalLive > 0 ? 'text-green-500' : 'text-muted-foreground'}`} />
        <h3 className="flex-1 text-sm font-semibold text-foreground">Live Meetings</h3>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground/50">
            <RefreshCw className={`h-2.5 w-2.5 ${connected ? 'text-green-500/50' : ''}`} />
            {lastUpdated.toLocaleTimeString()}
          </span>
          <span className="relative flex items-center gap-1.5">
            {data.totalLive > 0 && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
            )}
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-500">
              {data.totalLive} active
            </span>
          </span>
        </span>
      </div>

      <div className="p-5">
        {data.meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground">No meetings currently in progress</p>
        ) : (
          <div className="space-y-2">
            {data.meetings.map((m) => (
              <div key={m.uuid} className="flex items-center gap-3 rounded-lg border border-border/50 px-4 py-3 hover:bg-secondary/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{m.topic}</p>
                  <p className="text-xs text-muted-foreground">
                    Host: {m.host} · Started {m.startTime ? new Date(m.startTime).toLocaleTimeString() : '—'}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users className="h-3 w-3" /> {m.participants}
                  </span>
                  {m.hasIssues && (
                    <span className="flex items-center gap-1 text-xs text-orange-500">
                      <AlertTriangle className="h-3 w-3" /> Issues
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground/60">New meetings may take 5–10 minutes to appear in the Zoom Dashboard API.</p>
      </div>
    </div>
  );
}
